<template>
  <div class="requests-page">
    <div class="sidebar-wrapper">
      <button class="hamburger" @click="sidebarOpen = !sidebarOpen">☰</button>
      <div class="sidebar" :class="{ open: sidebarOpen }">
        <div class="sidebar-header">
          <button class="close-btn" @click="sidebarOpen = false">✖</button>
          <p>{{ user.fullname }}</p>
          <p>{{ user.phone_number }}</p>
        </div>
        <ul class="sidebar-menu">

          <template v-if="user && user.user_type === 'Pet Owner'">
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li class="has-submenu" @click="petsOpen = !petsOpen">
              My Pets
              <span class="arrow" :class="{ 'arrow-open': petsOpen }">›</span>
            </li>
            <ul class="sub-menu" :class="{ 'sub-open': petsOpen }">
              <li @click="goToPage('YourPetsPage')">View My Pets</li>
              <li @click="goToPage('AddPetPage')">Add Pet</li>
              <li @click="goToPage('MeetingsPage')">Booking Requests</li>
            </ul>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

          <template v-else>
            <li @click="goToPage('DashboardPage')">Browse Pets</li>
            <li @click="goToPage('UsersFavouritesPage')">My Favourites</li>
            <li @click="goToPage('CalendarPage')">My Calendar</li>
            <li @click="goToPage('EditUserDashboard')">My Profile</li>
            <li @click="goToPage('LogoutPage')">Log Out</li>
          </template>

        </ul>
      </div>
    </div>

    <div class="page-wrapper">
      <div class="page-header">
        <h1 class="page-title">Petting Requests</h1>
        <p class="page-sub">Track all your pet meeting requests</p>
      </div>

      <div class="controls-row">
        <div class="tab-group">
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'all' }" @click="activeTab = 'all'">All</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'pending' }" @click="activeTab = 'pending'">Pending</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'accepted' }" @click="activeTab = 'accepted'">Accepted</button>
          <button class="tab-btn" :class="{ 'tab-active': activeTab === 'denied' }" @click="activeTab = 'denied'">Denied</button>
        </div>
      </div>

      <div v-if="filteredRequests.length === 0" class="empty-state">
        <div class="empty-icon">📅</div>
        <p class="empty-text">No meetings here</p>
        <p class="empty-sub">{{
          activeTab === 'all' ? 'Your booking requests will appear here' :
          activeTab === 'pending' ? 'No pending requests yet' :
          activeTab === 'accepted' ? 'No accepted requests yet' :
          'No denied requests yet'
        }}</p>
      </div>

      <div v-else class="requests-list">
        <div v-for="req in filteredRequests" :key="req.id" class="booking-card">
          <div class="booking-left">
            <div class="booking-date-block">
              <span class="booking-day">{{ formatDay(req.date) }}</span>
              <span class="booking-month">{{ formatMonth(req.date) }}</span>
            </div>
          </div>
          <div class="booking-middle">
            <p class="booking-pet-name">{{ req.pet_name }}</p>
            <p class="booking-time">🕐 {{ formatTime(req.time) }}</p>
          </div>
          <div class="booking-right">
            <span class="status-badge" :class="statusClass(req.confirmation)">
              {{ getStatusLabel(req.confirmation) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
const backendUrl = process.env.VUE_APP_API_URL;

export default {
  name: 'PetRequestsPage',
  props: {
    user: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      requests: [],
      sidebarOpen: false,
      activeTab: 'all',
      petsOpen: false,
    };
  },
  computed: {
    filteredRequests() {
      if (this.activeTab === 'all') return this.requests;
      if (this.activeTab === 'pending') return this.requests.filter(r => r.confirmation === null);
      if (this.activeTab === 'accepted') return this.requests.filter(r => r.confirmation === 1);
      if (this.activeTab === 'denied') return this.requests.filter(r => r.confirmation === 0);
      return this.requests;
    }
  },
  methods: {
    goToPage(pageName) {
      this.sidebarOpen = false;
      this.$emit('change-page', pageName);
    },
    async load() {
      try {
        const { data } = await axios.get(`${backendUrl}/api/meetings/requests`, { withCredentials: true });
        this.requests = data;
      } catch (e) {
        console.error('Failed to load requests', e);
      }
    },
    formatTime(timeStr) {
      const [h, m] = timeStr.split(':');
      let hour = parseInt(h, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      hour = hour % 12 || 12;
      return `${hour}:${m} ${ampm}`;
    },
    getStatusLabel(status) {
      if (status === 1) return 'Accepted';
      if (status === 0) return 'Denied';
      return 'Pending';
    },
    formatDay(dateStr) {
      return String(new Date(dateStr).getDate()).padStart(2, '0');
    },
    formatMonth(dateStr) {
      return new Date(dateStr).toLocaleString('default', { month: 'short' }).toUpperCase();
    },
    statusClass(status) {
      if (status === 1) return 'status-accepted';
      if (status === 0) return 'status-denied';
      return 'status-pending';
    },
  },
  mounted() {
    this.load();
  }
};
</script>

<style scoped>
* { margin: 0; padding: 0; box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

.requests-page { min-height: 100vh; background: #fffbe8; }

.page-wrapper { max-width: 600px; margin: 0 auto; padding: 60px 20px 40px; }

.page-header { margin-bottom: 28px; }
.page-title { font-size: 26px; font-weight: 800; color: #1a1a1a; letter-spacing: -0.4px; }
.page-sub { font-size: 14px; color: #aaa; font-weight: 500; margin-top: 4px; }

.controls-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
.tab-group { display: flex; background: #fff; border: 1px solid #f0e0ff; border-radius: 999px; padding: 4px; gap: 4px; }
.tab-btn { padding: 8px 16px; border-radius: 999px; border: none; background: none; font-size: 13px; font-weight: 700; color: #aaa; cursor: pointer; transition: all 0.18s ease; }
.tab-btn:hover { color: #4a1070; }
.tab-active { background: #eac7ff; color: #4a1070; }

.requests-list { display: flex; flex-direction: column; gap: 14px; }

.booking-card { background: #fff; border-radius: 20px; border: 1px solid #f0e0ff; padding: 18px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 4px 16px rgba(186,113,255,0.07); transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s; }
.booking-card:hover { transform: translateY(-3px); box-shadow: 0 10px 28px rgba(186,113,255,0.13); }

.booking-left { flex-shrink: 0; }
.booking-date-block { width: 52px; height: 58px; background: linear-gradient(135deg, #eac7ff, #dda6fd); border-radius: 14px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1px; }
.booking-day { font-size: 20px; font-weight: 800; color: #4a1070; line-height: 1; }
.booking-month { font-size: 10px; font-weight: 700; color: #7a30a8; letter-spacing: 0.06em; }

.booking-middle { flex: 1; min-width: 0; }
.booking-pet-name { font-size: 16px; font-weight: 800; color: #1a1a1a; margin: 0 0 4px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.booking-time { font-size: 13px; color: #999; font-weight: 600; margin: 0; }

.booking-right { flex-shrink: 0; }
.status-badge { font-size: 12px; font-weight: 700; padding: 5px 13px; border-radius: 999px; display: inline-block; letter-spacing: 0.02em; }
.status-accepted { background: #d4f5e2; color: #1a6b3c; }
.status-denied { background: #fde8e8; color: #991b1b; }
.status-pending { background: #fffbe8; color: #92610a; border: 1px solid #f6d860; }

.empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 10px; }
.empty-icon { font-size: 48px; opacity: 0.5; }
.empty-text { font-size: 17px; font-weight: 700; color: #333; margin: 0; }
.empty-sub { font-size: 13px; color: #bbb; margin: 0; }

@media (max-width: 480px) { .page-wrapper { padding: 60px 14px 30px; } .booking-pet-name { font-size: 14px; } .booking-card { padding: 14px 16px; } }
</style>